import { useMutation, useQuery } from "urql";
import { graphql } from "../../../../__generated__/gql";
import {
  CellContext,
  ColumnDef,
  PaginationState,
  createColumnHelper,
} from "@tanstack/react-table";
import { UserStatus } from "../../../../__generated__/gql/graphql";
import Badge, { BadgeColor } from "../../../../ui/Badge/Badge";
import { useMemo, useState } from "react";
import Table from "../../../../ui/Table/Table";
import Avatar from "../../../../ui/Avatar/Avatar";
import Button from "../../../../ui/Button/Button";
import styles from "./TeamMembersTable.module.scss";
import useUser from "../../../../providers/UserProvider/useUser";
import urls from "../../../../routes";
import LeaveTeamModal from "./LeaveTeamModal";
import { useDisclosure } from "../../../../ui/util/useDisclosure";
import DeleteTeamMemberModal from "./DeleteTeamMemberModal";

const TeamMembersTableQuery = graphql(/* GraphQL */ `
  query teamMembersTable($paginationOptions: OffsetPaginationOptions) {
    users(paginationOptions: $paginationOptions) {
      nodes {
        id
        status
        firstName
        lastName
        email
        imageUrl
      }
      totalCount
    }
  }
`);

const DeleteUserMutation = graphql(/* GraphQL */ `
  mutation deleteUser($id: String!) {
    deleteUser(id: $id) {
      id
    }
  }
`);

const LeaveTeamMutation = graphql(/* GraphQL */ `
  mutation leaveTeam {
    leaveTeam {
      id
    }
  }
`);

const RevokeInvitationMutation = graphql(/* GraphQL */ `
  mutation revokeInvitation($id: String!) {
    revokeInvitation(id: $id) {
      id
    }
  }
`);

const ReinviteUserMutation = graphql(/* GraphQL */ `
  mutation reinviteUser($email: String!) {
    inviteUsers(input: { users: [{ email: $email }] }) {
      id
    }
  }
`);

type TeamMemberCellProps = {
  id: string;
  firstName?: string;
  lastName?: string;
  status: UserStatus;
  email: string;
  imageUrl?: string;
};

function formatUserStatus(status: UserStatus): string {
  switch (status) {
    case UserStatus.Invited:
      return "Invited";
    case UserStatus.Active:
      return "Active";
    case UserStatus.Disabled:
      return "Disabled";
    default:
      return "Unknown";
  }
}

function NameCell({ row }: CellContext<TeamMemberCellProps, unknown>) {
  const { firstName, lastName, imageUrl, email } = row.original;

  return (
    <Avatar
      imageUrl={imageUrl}
      firstName={firstName}
      lastName={lastName}
      email={email}
      hideTooltip
      showNameAndEmail
    />
  );
}

function StatusCell({ cell }: CellContext<TeamMemberCellProps, UserStatus>) {
  const status = cell.getValue();
  const color: BadgeColor = useMemo(() => {
    switch (status) {
      case UserStatus.Invited:
        return "warning";
      case UserStatus.Active:
        return "success";
      case UserStatus.Disabled:
        return "default";
      default:
        return "default";
    }
  }, [status]);

  return <Badge color={color}>{formatUserStatus(status)}</Badge>;
}

function ActionCell({ row, table }: CellContext<TeamMemberCellProps, unknown>) {
  const { user } = useUser();
  const [deleteUserResult, deleteUser] = useMutation(DeleteUserMutation);
  const [leaveTeamResult, leaveTeam] = useMutation(LeaveTeamMutation);
  const [revokeInvitationResult, revokeInvitation] = useMutation(
    RevokeInvitationMutation,
  );
  const [reinviteUserResult, reinviteUser] = useMutation(ReinviteUserMutation);
  const [leaveTeamModalOpened, leaveTeamModal] = useDisclosure();
  const [deleteTeamMemberModalOpened, deleteTeamMemberModal] = useDisclosure();

  const isLastUser = table.getRowModel().rows.length === 1;

  if (isLastUser) {
    return null;
  }

  return (
    <div className={styles.actionCell}>
      {row.original.status === UserStatus.Active &&
        (user?.id === row.original.id ? (
          <Button variant="link" onClick={leaveTeamModal.open}>
            Leave
          </Button>
        ) : (
          <Button variant="link" onClick={deleteTeamMemberModal.open}>
            Delete
          </Button>
        ))}
      {row.original.status === UserStatus.Invited && (
        <Button
          variant="link"
          loading={revokeInvitationResult.fetching}
          onClick={() => revokeInvitation({ id: row.original.id })}
        >
          Revoke
        </Button>
      )}
      {row.original.status === UserStatus.Disabled && (
        <Button
          variant="link"
          loading={reinviteUserResult.fetching}
          onClick={() => reinviteUser({ email: row.original.email })}
        >
          Reinvite
        </Button>
      )}
      <LeaveTeamModal
        onClose={leaveTeamModal.close}
        isOpen={leaveTeamModalOpened}
        loading={leaveTeamResult.fetching}
        onSubmit={async () => {
          const leaveTeamResponse = await leaveTeam({});

          if (leaveTeamResponse.data) {
            window.location.href = urls.home;
          }
        }}
      />
      <DeleteTeamMemberModal
        onClose={deleteTeamMemberModal.close}
        isOpen={deleteTeamMemberModalOpened}
        loading={deleteUserResult.fetching}
        onSubmit={async () => {
          await deleteUser({ id: row.original.id });
        }}
      />
    </div>
  );
}

const DEFAULT_PAGINATION_STATE: PaginationState = {
  pageIndex: 0,
  pageSize: 25,
};

function TeamMembersTable() {
  const [pagination, setPagination] = useState<PaginationState>(
    DEFAULT_PAGINATION_STATE,
  );
  const [{ data, fetching }] = useQuery({
    query: TeamMembersTableQuery,
    variables: {
      paginationOptions: {
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
      },
    },
  });
  const columnHelper = createColumnHelper<TeamMemberCellProps>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<TeamMemberCellProps, any>[] = [
    columnHelper.display({
      header: "Name",
      cell: NameCell,
    }),
    columnHelper.accessor("status", {
      header: "Status",
      cell: StatusCell,
      size: 111,
    }),
    columnHelper.display({
      id: "userActions",
      cell: ActionCell,
      size: 131,
    }),
  ];

  return (
    <div className={styles.container}>
      <Table
        loading={fetching}
        data={
          data?.users.nodes.map((user) => ({
            id: user.id,
            email: user.email,
            imageUrl: user.imageUrl ?? undefined,
            firstName: user.firstName ?? undefined,
            lastName: user.lastName ?? undefined,
            status: user.status,
          })) || []
        }
        columns={columns}
        pagination={pagination}
        setPagination={setPagination}
      />
    </div>
  );
}

export default TeamMembersTable;
